import { useState } from "react";
import { X, ArrowLeft, MapPin } from "lucide-react";
import { Pitch } from "@/components/Pitch";
import { TargetPicker } from "@/components/TargetPicker";
import { SubstitutionPicker } from "@/components/SubstitutionPicker";
import { NoteStep } from "@/components/NoteStep";
import { toast } from "sonner";
import { useIsMobile } from "@/lib/useMobile";
import { EVENT_MAP, formatTime, eventMinute, priorYellows, targetLabel } from "@/lib/events";

const stepLabel = (type, zone, draft) => {
  if (type.flow === "sub") return "Jogador que sai e jogador que entra";
  if (!zone) return type.zoneOnly ? "Toque na zona onde foi assinalado" : "Passo 1 · Zona do campo";
  if (draft) return "Passo 3 · Observação";
  if (type.key === "autogolo") return "Jogador que marcou na própria baliza";
  if (type.key === "golo") return "Jogador que marcou o golo";
  return "Passo 2 · Jogador ou oficial";
};

export const EventModal = ({ pending, match, onCancel, onSave }) => {
  const type = EVENT_MAP[pending.type];
  const isMobile = useIsMobile();
  const [zone, setZone] = useState(type.noZone || pending.halftime || pending.phase ? {} : null);
  const [draft, setDraft] = useState(null);

  const pickTarget = (target) => {
    if (type.key === "amarelo" && priorYellows(match, target) > 0)
      toast.warning(`Atenção: 2º amarelo a ${targetLabel(target)} (${match[target.team].name})`, { duration: 6000 });
    type.note ? setDraft({ ...zone, ...target }) : onSave({ ...pending, ...zone, ...target });
  };
  const secondYellow = type.key === "amarelo" && draft && priorYellows(match, draft) > 0;

  const body = () => {
    if (type.flow === "sub") return <SubstitutionPicker match={match} halftimeDefault={!!pending.halftime} onSave={(list) => onSave(list.map((t) => ({ ...pending, ...t })))} />;
    if (!zone)
      return isMobile ? (
        <>
          <p className="label-caps text-center mb-2">↑ {match.home.name}</p>
          <div className="mx-auto" style={{ maxWidth: "calc((100vh - 190px) * 0.66)" }}>
            <Pitch vertical interactive onPick={type.zoneOnly ? (z) => onSave({ ...pending, ...z }) : setZone} testId="event-pitch" />
          </div>
          <p className="label-caps text-center mt-2">{match.away.name} ↓</p>
        </>
      ) : (
        <>
          <div className="flex justify-between label-caps mb-3 px-1">
            <span>← {match.home.name}</span><span>Toque na zona onde ocorreu</span><span>{match.away.name} →</span>
          </div>
          <div className="mx-auto" style={{ maxWidth: "max(320px, calc((100vh - 230px) * 1.5))" }}>
            <Pitch interactive onPick={type.zoneOnly ? (z) => onSave({ ...pending, ...z }) : setZone} testId="event-pitch" />
          </div>
        </>
      );
    return (
      <>
        {!type.noZone && <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-5 sm:mb-8">
          {(zone.zone || draft) && <button data-testid="event-modal-back" onClick={() => (draft ? setDraft(null) : setZone(null))}
            className="tap h-11 px-3 sm:px-4 border border-white/20 flex items-center gap-2 text-sm font-semibold hover:bg-white/10">
            <ArrowLeft size={16} /> {draft ? "Identificação" : "Zona"}
          </button>}
          {zone.zone && <>
            <span data-testid="event-modal-zone" className="h-11 px-3 sm:px-4 bg-[#CCFF00] text-black flex items-center gap-2 font-display text-xl font-bold">
              <MapPin size={16} /> {zone.zone}
            </span>
            <div className="w-28 hidden sm:block"><Pitch picked={zone} showZones={false} testId="event-pitch-mini" /></div>
          </>}
          {!draft && !type.playersOnly && (
            <button data-testid="event-save-no-target" onClick={() => pickTarget({})}
              className="tap ml-auto h-11 px-3 sm:px-4 border border-white/20 text-sm font-semibold text-zinc-300 hover:bg-white/10">
              Sem identificação
            </button>
          )}
        </div>}
        {secondYellow && (
          <div data-testid="second-yellow-alert" className="rise mb-6 p-4 border-2 border-[#FF3B30] bg-[#2a0d0b] flex items-center gap-4">
            <span className="flex gap-1 shrink-0"><span className="w-5 h-7 bg-[#FFCC00]" /><span className="w-5 h-7 bg-[#FFCC00]" /></span>
            <div>
              <p className="font-display text-2xl font-bold uppercase text-[#FF3B30]">Segundo amarelo</p>
              <p className="text-sm text-zinc-200">{targetLabel(draft)} ({match[draft.team].name}) já tem um cartão amarelo. Ao guardar, será registado automaticamente o cartão vermelho.</p>
            </div>
          </div>
        )}
        {draft ? (
          <NoteStep draft={draft} match={match} onSave={(note) => onSave({ ...pending, ...draft, note: note || null })} />
        ) : (
          <TargetPicker match={match} playersOnly={type.playersOnly} onSelect={pickTarget} />
        )}
      </>
    );
  };

  return (
    <div data-testid="event-modal" className="fixed inset-0 z-50 bg-black/85 backdrop-blur-md overflow-y-auto">
      <div className="rise max-w-6xl mx-auto p-2 sm:p-8">
        <div className="sticky top-0 z-10 flex items-center justify-between gap-3 mb-2 sm:mb-6 bg-[#0A0A0A] border border-white/15 p-3 sm:p-4">
          <div className="flex items-center gap-3 sm:gap-4 min-w-0">
            <span className="w-2 sm:w-3 h-10 shrink-0" style={{ background: type.color }} />
            <div className="min-w-0">
              <p data-testid="event-modal-step" className="label-caps truncate">{stepLabel(type, zone, draft)}</p>
              <h2 data-testid="event-modal-title" className="font-display text-2xl sm:text-4xl font-bold uppercase truncate">{type.label}</h2>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 shrink-0">
            <span data-testid="event-modal-time" className="font-display text-2xl sm:text-3xl font-bold text-[#CCFF00] tabular-nums">
              {eventMinute(pending, match.half2_reset)}
            </span>
            <span className="hidden sm:inline text-sm text-zinc-500 tabular-nums">{formatTime(pending.seconds)}</span>
            <button data-testid="event-modal-close" onClick={onCancel} className="tap w-12 h-12 border border-white/20 flex items-center justify-center hover:bg-white/10">
              <X size={22} />
            </button>
          </div>
        </div>
        <div className="bg-[#0A0A0A] border border-white/15 p-3 sm:p-6">{body()}</div>
      </div>
    </div>
  );
};
